import { useCallback } from 'react'
import { useWardrobe } from '../../../../../hooks/useWardrobe'

/**
 * Centralized hook for outfit library data access.
 * Reads from formData.outfits and only fetches when data is empty.
 * Supports pagination for loading more items.
 *
 * @param {Object} options
 * @param {Object} options.formData - Form data containing outfits and outfitsPagination
 * @param {Function} options.setFormData - Function to update form data
 * @returns {Object} Library state and actions
 */
export const useOutfitLibrary = ({ formData = {}, setFormData = () => {} }) => {
  const { isLoading, fetchLibrary, stopPolling } = useWardrobe()

  const outfits = formData?.outfits || []
  const pagination = formData?.outfitsPagination || null
  const hasData = outfits.length > 0

  /**
   * Fetch library only if no data exists
   */
  const fetchIfNeeded = useCallback(async () => {
    if (!hasData && !isLoading) {
      try {
        const result = await fetchLibrary({ page: 1 })
        if (result && setFormData) {
          setFormData(prev => ({
            ...prev,
            outfits: result.items || [],
            outfitsPagination: result.pagination || null
          }))
        }
        return result?.items || []
      } catch (err) {
        console.error('Failed to fetch library:', err)
        return null
      }
    }
    return outfits
  }, [hasData, isLoading, fetchLibrary, setFormData, outfits])

  /**
   * Force refresh library data (for manual refresh button)
   */
  const refresh = useCallback(
    async (params = {}) => {
      try {
        const result = await fetchLibrary({ page: 1, ...params })
        if (result && setFormData) {
          setFormData(prev => ({
            ...prev,
            outfits: result.items || [],
            outfitsPagination: result.pagination || null
          }))
        }
        return result?.items || []
      } catch (err) {
        console.error('Failed to refresh library:', err)
        return null
      }
    },
    [fetchLibrary, setFormData]
  )

  /**
   * Load more items (next page)
   */
  const loadMore = useCallback(async () => {
    if (!pagination?.has_next || isLoading) return null

    const nextPage = (pagination?.page || 1) + 1
    try {
      const result = await fetchLibrary({ page: nextPage })
      if (result && setFormData) {
        setFormData(prev => ({
          ...prev,
          // Append new items to existing ones
          outfits: [...(prev.outfits || []), ...(result.items || [])],
          outfitsPagination: result.pagination || null
        }))
      }
      return result?.items || []
    } catch (err) {
      console.error('Failed to load more:', err)
      return null
    }
  }, [pagination, isLoading, fetchLibrary, setFormData])

  /**
   * Jump to a specific page (replaces current outfits)
   */
  const goToPage = useCallback(
    async page => {
      try {
        const result = await fetchLibrary({ page })
        if (result && setFormData) {
          setFormData(prev => ({
            ...prev,
            outfits: result.items || [],
            outfitsPagination: result.pagination || null
          }))
        }
        return result?.items || []
      } catch (err) {
        console.error(`Failed to go to page ${page}:`, err)
        return null
      }
    },
    [fetchLibrary, setFormData]
  )

  /**
   * Search outfits with query
   */
  const search = useCallback(
    async query => {
      return refresh({ action: 'search', query })
    },
    [refresh]
  )

  /**
   * Filter outfits by style/season
   */
  const filter = useCallback(
    async params => {
      return refresh({ action: 'filter', ...params })
    },
    [refresh]
  )

  /**
   * Find outfit by tag or id
   */
  const findByTag = useCallback(
    tag => {
      return outfits.find(o => (o.tag || o.id) === tag) || null
    },
    [outfits]
  )

  return {
    outfits,
    pagination,
    hasData,
    hasMore: pagination?.has_next || false,
    isLoading,
    fetchIfNeeded,
    refresh,
    loadMore,
    goToPage,
    search,
    filter,
    findByTag,
    stopPolling
  }
}

export default useOutfitLibrary
